import { useMemo, useState } from 'react'

import { CALLING_CODES } from '@ems/contracts/calling-codes'
import { countries } from '@ems/contracts/countries'
import { callingCode, joinPhone, parsePhone } from '@ems/contracts/phone'

import { cn } from 'cn'
import { Input } from '#ui/components/input.tsx'
import { Combobox } from '#ui/patterns/combobox.tsx'

type Parts = { country: string; national: string }

function partsOf(value: string, fallbackCountry: string, hint?: string): Parts {
  const parsed = value === '' ? undefined : parsePhone(value, hint)
  return parsed ?? { country: fallbackCountry, national: '' }
}

/**
 * A phone number: a country's calling code beside the national digits, stored as one E.164
 * string (`+6281234567890`).
 *
 * **Two controls, one value.** The code picker is searchable by country name, code or calling code
 * ("indo", "ID", "62"); the digits are typed as people write them — `0812-3456-7890` is fine, the
 * leading `0` is dropped on the way out (`joinPhone`). The edition's own country is the default,
 * because that is where most of its delegates' numbers are from.
 *
 * The trigger shows `ID +62` as text rather than a flag. Flags are emoji, and Windows renders a
 * flag emoji as two letters in boxes — the one platform most operators use.
 *
 * The `id` a `Field` hands over goes on the digits, which is what the label is about; the picker
 * carries its own label, "Country code".
 */
export function PhoneInput({
  value,
  onChange,
  defaultCountry,
  disabled,
  className,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  required,
}: {
  value: string
  onChange: (value: string) => void
  defaultCountry: string
  disabled?: boolean
  className?: string
  id?: string
  'aria-describedby'?: string | undefined
  'aria-invalid'?: boolean
  required?: boolean
}) {
  const [parts, setParts] = useState<Parts>(() => partsOf(value, defaultCountry))
  const [emitted, setEmitted] = useState(value)

  // A value from outside — a reset, a record loaded into the form — replaces what is typed. The
  // component's own emissions are recognized and left alone, so typing is never re-parsed mid-word.
  // (Adjusting state while rendering, React's documented pattern for "state derived from a prop".)
  if (value !== emitted) {
    setEmitted(value)
    setParts(partsOf(value, defaultCountry, parts.country))
  }

  const options = useMemo(
    () =>
      countries()
        .filter((country) => CALLING_CODES[country.code] !== undefined)
        .map((country) => {
          const code = callingCode(country.code) ?? ''
          return {
            value: country.code,
            label: country.name,
            hint: `+${code}`,
            keywords: [country.code, code, `+${code}`],
          }
        }),
    [],
  )

  const emit = (next: Parts) => {
    setParts(next)
    const joined =
      next.national.replaceAll(/\D/g, '') === '' ? '' : joinPhone(next.country, next.national)
    setEmitted(joined)
    onChange(joined)
  }

  return (
    <div className={cn('flex w-full min-w-0', className)}>
      <Combobox
        value={parts.country}
        onValueChange={(country) => emit({ ...parts, country })}
        options={options}
        aria-label="Country code"
        searchPlaceholder="Search country or code…"
        emptyText="No country matches."
        renderValue={(option) => (
          <span className="tabular-nums">
            {option.value} {option.hint}
          </span>
        )}
        disabled={disabled}
        className="w-auto shrink-0 rounded-r-none border-r-0"
        contentClassName="w-72"
      />
      <Input
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        value={parts.national}
        onChange={(event) => emit({ ...parts, national: event.target.value })}
        disabled={disabled}
        className="min-w-0 flex-1 rounded-l-none"
        maxLength={24}
        {...(id === undefined ? {} : { id })}
        {...(ariaDescribedBy === undefined ? {} : { 'aria-describedby': ariaDescribedBy })}
        {...(ariaInvalid === undefined ? {} : { 'aria-invalid': ariaInvalid })}
        {...(required === undefined ? {} : { required })}
      />
    </div>
  )
}
